import {
	type Control,
	Controller,
	type FieldPath,
	type FieldValues,
} from "react-hook-form";
import {
	FlatRowInput,
	type FlatRowInputProps,
} from "web_myplann/Shared/Components/FlatRow/FlatRowInput";

import { FloatFlatRowInput } from "web_myplann/Shared/Components/FlatRow/FloatFlatRowInput";
import { limitDigits } from "web_myplann/Shared/Utils/limitDigits";
import {
	formatThousands,
	parseThousands,
} from "web_myplann/Shared/Utils/numberFormatters";

type TypedValueProps =
	| { type: "number"; max?: number }
	| { type: "float"; max?: number }
	| { type?: "text"; max?: never };

type Props<
	FormType extends FieldValues,
	TName,
	TContext,
	TTransformed extends FieldValues | undefined,
> = Omit<
	FlatRowInputProps,
	"value" | "onChange" | "onBlur" | "inputRef" | "error" | "helperText"
> & {
	control: Control<FormType, TContext, TTransformed>;
	name: TName;
	decimalPlaces?: number;
	maxLength?: number;
	max?: number;
	deps?: FieldPath<FormType>[];
	onValueChange?: (value: string) => void;
} & TypedValueProps;

export const FlatRowFormInput = <
	FormType extends FieldValues,
	TName extends FieldPath<FormType>,
	TContext = unknown,
	TTransformed extends FieldValues | undefined = undefined,
>({
	control,
	name,
	type = "text",
	inputMode,
	decimalPlaces,
	maxLength,
	max,
	deps,
	onValueChange,
	...rest
}: Props<FormType, TName, TContext, TTransformed>) => {
	const isNumber = type === "number";
	const isFloat = type === "float";
	// the underlying multiline input has no native type — numeric modes are driven
	// by inputMode plus the formatting/parsing done here and in FloatFlatRowInput
	const effectiveInputMode = isNumber
		? "numeric"
		: isFloat
			? "decimal"
			: inputMode;

	return (
		<Controller
			control={control}
			name={name}
			rules={deps ? { deps } : undefined}
			render={({
				field: { value, onChange, onBlur, ref },
				fieldState: { error },
			}) => {
				if (isFloat) {
					return (
						<FloatFlatRowInput
							{...rest}
							inputMode={effectiveInputMode}
							decimalPlaces={decimalPlaces}
							max={max}
							value={value}
							onChange={onChange}
							onBlur={onBlur}
							inputRef={ref}
							error={!!error?.message}
							helperText={error?.message}
						/>
					);
				}

				const stringValue = value == null ? "" : String(value);
				const displayValue = isNumber
					? formatThousands(stringValue)
					: stringValue;

				const handleChange = (next: string) => {
					if (isNumber) {
						const limited =
							max == null ? next : limitDigits(next, max, stringValue);
						const parsed = parseThousands(limited) ?? null;
						onChange(parsed);
						onValueChange?.(parsed === null ? "" : String(parsed));
					} else {
						const limited =
							maxLength === undefined ? next : next.slice(0, maxLength);
						onChange(limited);
						onValueChange?.(limited);
					}
				};

				return (
					<FlatRowInput
						{...rest}
						inputMode={effectiveInputMode}
						value={displayValue}
						onChange={handleChange}
						onBlur={onBlur}
						inputRef={ref}
						error={!!error?.message}
						helperText={error?.message}
					/>
				);
			}}
		/>
	);
};
